import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Cart Drawer Component — Fixed version
 * - Synchronized with cart-upsell component
 * - Proper event dispatching for cart updates
 * - Fixed race conditions
 */

class CartDrawerComponent extends DialogComponent {
  #dialog;
  #overlay;
  #scrollY = 0;
  #isLocked = false;
  #itemsObserver;
  #summaryObserver;
  #upsellDidInit = false;
  #lastCart = null;
  #didLog = false;
  #updateTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog = this.querySelector('dialog');
    this.#overlay = document.querySelector('.additional-checkout-button__overlay') || null;

    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    
    // Listen for cart updates from any source
    document.addEventListener('cart:update', this.#onCartUpdate);

    this.#dialog?.addEventListener('close', this.#onClose);
    this.#dialog?.addEventListener('cancel', this.#onClose);

    if (this.#dialog) {
      const mo = new MutationObserver(() => {
        if (this.#dialog.hasAttribute('open')) {
          requestAnimationFrame(() => {
            this.#lockBody('open attr');
            this.#armUpsellObservers();
            this.#log('dialog opened (attr observer)');
          });
        }
      });
      mo.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });
      this._openAttrObserver = mo;
    }

    if (this.#overlay) {
      this.#overlay.addEventListener('click', (e) => {
        if (e.target === this.#overlay && this.#overlay.classList.contains('active')) {
          this.showQuickPayOverlay(false);
        }
      });
    }

    if (window.DEBUG_CART_DRAWER) this.#log('CartDrawerComponent connected');

    // Check if an iphone, in which case, show apple pay:
    if (/iPhone/i.test(navigator.userAgent)) {
      this.classList.add('apple_pay')
    } 
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('cart:update', this.#onCartUpdate);

    this.#dialog?.removeEventListener('close', this.#onClose);
    this.#dialog?.removeEventListener('cancel', this.#onClose);

    this._openAttrObserver?.disconnect();
    this.#disarmUpsellObservers();
    
    if (this.#updateTimeout) {
      clearTimeout(this.#updateTimeout);
    }

    if (window.DEBUG_CART_DRAWER) this.#log('CartDrawerComponent disconnected');
  }

  /* ========= Public API ========= */

  open() {
    this.showDialog();

    requestAnimationFrame(() => {
      this.#lockBody('manual open()');
      this.#armUpsellObservers();
      this.#log('open() finished');
    });

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const sr = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = sr?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    this.#onClose();
  }

  showQuickPayOverlay(show) {
    if (!this.#overlay) this.#overlay = document.querySelector('.additional-checkout-button__overlay');
    if (!this.#overlay) return;

    this.#overlay.classList.toggle('active', !!show);

    if (show) {
      this.#lockBody('overlay open');
    } else {
      if (!this.#dialog?.hasAttribute('open')) {
        this.#unlockBody('overlay close');
      }
    }
  }

  /* ========= Events ========= */

  #onCartAdd = (e) => {
    this.#lastCart = e?.detail?.cart || this.#lastCart;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      requestAnimationFrame(() => {
        this.#lockBody('auto-open (CartAddEvent)');
        this.#armUpsellObservers();
        this.#log('auto-open after add to cart');
      });
    }
    
    // Reset upsell flag so it re-initializes with new cart data
    this.#upsellDidInit = false;
    
    // Dispatch cart:update for other components
    // document.dispatchEvent(new CustomEvent('cart:update', { 
    //   detail: { cart: this.#lastCart } 
    // }));
  };

  #onCartUpdate = (e) => {
    this.#log('cart:update received');
    
    
    // Debounce updates to avoid rapid re-renders
    if (this.#updateTimeout) {
      clearTimeout(this.#updateTimeout);
    }
    
    this.eventPassed = e;
    this.#updateTimeout = setTimeout(async () => {
      // Only refresh upsell if drawer is open
      if (this.#dialog?.hasAttribute('open')) {
        this.#upsellDidInit = false;
        this.#lastCart = null; // Force fresh fetch
        await this.#armUpsellObservers();
        this.#log('upsell refreshed after cart update');

        // Trigger SHipping protection update (unless it comes from itself):
        if (!this.eventPassed?.detail?.data?.shippingProtection)
            this.querySelector('shipping-protection')?.handleProtectionLogic()
      }
    }, 150); // Slightly longer delay to ensure cart is updated
  };

  #onClose = () => {
    this.#unlockBody('dialog close');
    this.showQuickPayOverlay(false);
    this.#upsellDidInit = false;
    this.#lastCart = null;
    this.#disarmUpsellObservers();
    
    if (this.#updateTimeout) {
      clearTimeout(this.#updateTimeout);
    }
    
    this.#log('closed, cleaned up');
  };

  /* ========= Body lock (iOS-safe, no touch handlers) ========= */

  #lockBody(reason = '') {
    if (this.#isLocked) { this.#log(`lock skipped (already locked) — ${reason}`); return; }
    this.#isLocked = true;

    this.#scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('drawer-open');
    document.body.classList.add('drawer-open');

    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${this.#scrollY}px`,
      left: '0',
      right: '0',
      width: '100%',
      overscrollBehavior: 'contain',
      touchAction: 'none',
    });

    setTimeout(() => {
      if (this.#isLocked && getComputedStyle(document.body).position !== 'fixed') {
        this.#log('lock failsafe re-applied');
        Object.assign(document.body.style, {
          position: 'fixed',
          top: `-${this.#scrollY}px`,
          left: '0',
          right: '0',
          width: '100%',
          overscrollBehavior: 'contain',
          touchAction: 'none',
        });
      }
    }, 300);

    this.#log(`body locked — ${reason}`);
  }

  #unlockBody(reason = '') {
    if (!this.#isLocked) { this.#log(`unlock skipped (not locked) — ${reason}`); return; }
    this.#isLocked = false;

    document.documentElement.classList.remove('drawer-open');
    document.body.classList.remove('drawer-open');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overscrollBehavior = '';
    document.body.style.touchAction = '';

    window.scrollTo(0, this.#scrollY || 0);
    this.#log(`body unlocked — ${reason}`);
  }

  /* ========= Upsell init (race-free, no polling) ========= */

  async #armUpsellObservers() {
    if (this.#itemsObserver || this.#summaryObserver) {
      this.#disarmUpsellObservers();
    }

    const tryInit = async () => {
      if (this.#upsellDidInit) return;

      const items = this.querySelector('.cart-drawer__items');
      const summary = this.querySelector('.cart-drawer__summary');

      if (items && summary) {
        // Wait for DOM to settle
        await new Promise(r => requestAnimationFrame(r));
        await new Promise(r => setTimeout(r, 100));

        const cart = this.#lastCart || (await this.#safeFetchCart());

        // Dispatch event for upsell component
        this.dispatchEvent(new CustomEvent('cartdrawer:ready', { 
          bubbles: true, 
          detail: { cart } 
        }));

        // Global upsell hook
        if (window.Upsell && typeof window.Upsell.render === 'function') {
          try { 
            window.Upsell.render({ cart }); 
          } catch (err) { 
            this.#log('Upsell.render error', err); 
          }
        }

        this.#upsellDidInit = true;
        this.#log('upsell initialized', cart);
        this.#disarmUpsellObservers();
      }
    };

    tryInit();

    this.#itemsObserver = new MutationObserver(tryInit);
    this.#summaryObserver = new MutationObserver(tryInit);

    const itemsRoot = this.querySelector('.cart-drawer__scrollable') || this;
    const summaryRoot = this;

    this.#itemsObserver.observe(itemsRoot, { childList: true, subtree: true });
    this.#summaryObserver.observe(summaryRoot, { childList: true, subtree: true });

    this.#log('upsell observers armed');
  }

  #disarmUpsellObservers() {
    this.#itemsObserver?.disconnect();
    this.#summaryObserver?.disconnect();
    this.#itemsObserver = null;
    this.#summaryObserver = null;
    this.#log('upsell observers disarmed');
  }

  async #safeFetchCart() {
    try {
      const res = await fetch('/cart.js', { 
        headers: { 'Accept': 'application/json' },
        cache: 'no-cache'
      });
      if (res.ok) {
        const cart = await res.json();
        this.#log('fetched cart', cart);
        return cart;
      }
    } catch (err) {
      this.#log('fetch cart failed', err);
    }
    return null;
  }

  /* ========= Debug ========= */

  #log(...args) {
    if (!window.DEBUG_CART_DRAWER) return;
    if (!this.#didLog) {
      this.#didLog = true;
      console.info('%c[CartDrawer]', 'color:#fff;background:#111;padding:2px 6px;border-radius:3px', 'debug enabled');
    }
    console.info('[CartDrawer]', ...args);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}